import { useRef, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { MAX_CASE_FILES } from '@/lib/api-types'
import { validateCaseFiles, type FileValidationError } from '@/lib/files'
import { cn } from '@/lib/utils'
import { FileChip } from './file-chip'

/**
 * Multi-file drag-drop zone for request attachments. Client-side validation
 * mirrors the API (extension allowlist, 10 MB each, 5 per request, duplicate
 * names rejected) via validateCaseFiles; every rejection is announced inline
 * with its reason — nothing fails silently.
 */
export function MultiFileDropZone({
  files,
  onChange,
  inputLabel = 'Attach files',
}: {
  files: File[]
  onChange: (files: File[]) => void
  /** Accessible name for the hidden file input. */
  inputLabel?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [rejections, setRejections] = useState<FileValidationError[]>([])

  function accept(incoming: File[]) {
    if (incoming.length === 0) return
    const { accepted, rejected } = validateCaseFiles(
      incoming,
      files.map((file) => file.name),
    )
    setRejections(rejected)
    if (accepted.length > 0) onChange([...files, ...accepted])
  }

  function remove(name: string) {
    onChange(files.filter((file) => file.name !== name))
    setRejections([])
  }

  return (
    <div>
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          accept(Array.from(event.dataTransfer.files))
        }}
        className={cn(
          'rounded-lg border border-dashed px-6 py-8 text-center transition-colors duration-[180ms] ease-editorial',
          dragging ? 'border-brand bg-brandTint/40' : 'border-rule bg-cream/60',
        )}
      >
        <Paperclip aria-hidden="true" className="mx-auto size-5 text-mute" />
        <p className="mt-3 text-sm text-inkMid">
          Drag files here, or{' '}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="rounded-sm font-semibold text-brand underline-offset-4 hover:underline"
          >
            browse your computer
          </button>
        </p>
        <p className="mt-1.5 font-mono text-[11px] text-mute">
          up to {MAX_CASE_FILES} files · 10 MB each · pdf, images, office docs, text, zip
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          aria-label={inputLabel}
          className="sr-only"
          onChange={(event) => {
            accept(Array.from(event.target.files ?? []))
            event.target.value = ''
          }}
        />
      </div>

      {rejections.length > 0 ? (
        <ul role="alert" className="mt-2.5 space-y-1 text-xs font-medium text-rust">
          {rejections.map((rejection) => (
            <li key={rejection.name}>
              {rejection.name}: {rejection.reason}.
            </li>
          ))}
        </ul>
      ) : null}

      {files.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {files.map((file) => (
            <li key={file.name}>
              <FileChip file={file} onRemove={() => remove(file.name)} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
